<template>
  <div v-if="image" 
       class="fullscreen-overlay"
       @touchstart="handleTouchStart"
       @touchmove="handleTouchMove"
       @touchend="handleTouchEnd"
       @mousedown="handleMouseDown"
       @mousemove="handleMouseMove"
       @mouseup="handleMouseUp"
       @mouseleave="handleMouseUp">
    <button class="close-button" @click="onClose">←</button>
    
    <!-- Layered image container -->
    <div class="swipe-container" ref="swipeContainer">
      <!-- Top layer (previous image) -->
      <div class="swipe-layer top-layer" :style="topLayerStyle" v-if="previousImage">
        <div class="fullscreen-content">
          <img :src="previousImage.imgurl" :alt="previousImage.title" class="fullscreen-image">
          <div class="fullscreen-caption">
            <h3>{{ previousImage.title }}</h3>
            <a 
              :href="previousImage.click ? `/v1/api/click?${previousImage.click}` : previousImage.url" 
              target="_blank"
              class="source-link"
              @click.stop
            >
              Visit Source
            </a>
          </div>
        </div>
      </div>
      
      <!-- Visible layer (current image) -->
      <div class="swipe-layer visible-layer" :style="visibleLayerStyle">
        <div class="fullscreen-content">
          <img :src="image.imgurl" :alt="image.title" class="fullscreen-image">
          <div class="fullscreen-caption">
            <h3>{{ image.title }}</h3>
            <a 
              :href="image.click ? `/v1/api/click?${image.click}` : image.url" 
              target="_blank"
              class="source-link"
              @click.stop
            >
              Visit Source
            </a>
          </div>
        </div>
      </div>
      
      <!-- Bottom layer (next image) -->
      <div class="swipe-layer bottom-layer" :style="bottomLayerStyle" v-if="nextImage">
        <div class="fullscreen-content">
          <img :src="nextImage.imgurl" :alt="nextImage.title" class="fullscreen-image">
          <div class="fullscreen-caption">
            <h3>{{ nextImage.title }}</h3>
            <a 
              :href="nextImage.click ? `/v1/api/click?${nextImage.click}` : nextImage.url" 
              target="_blank"
              class="source-link"
              @click.stop
            >
              Visit Source
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType, watch as vueWatch } from 'vue';

export interface ImageItem {
  title: string;
  url: string;
  click?: string;
  imgurl?: string;
  headline?: string;
  description?: string;
  photo?: string;
}

export default defineComponent({
  name: 'FullscreenImage',
  props: {
    image: {
      type: Object as PropType<ImageItem>,
      default: null
    },
    getNextImage: {
      type: Function as PropType<(image: ImageItem) => ImageItem | null>,
      required: true
    },
    getPreviousImage: {
      type: Function as PropType<(image: ImageItem) => ImageItem | null>,
      required: true
    }
  },
  emits: ['close', 'change-image'],
  setup(props, { emit }) {
    const swipeContainer = ref<HTMLDivElement | null>(null);
    const swipeOffset = ref(0);
    const isDragging = ref(false);
    const startY = ref(0);
    const currentY = ref(0);
    const swipeThreshold = 0.1; // 10% of screen height
    
    const previousImage = ref<ImageItem | null>(null);
    const nextImage = ref<ImageItem | null>(null);
    
    // Update previous and next images when current image changes
    const updateAdjacentImages = () => {
      if (props.image) {
        previousImage.value = props.getPreviousImage(props.image);
        nextImage.value = props.getNextImage(props.image);
        console.log('Adjacent images updated', {
          current: props.image.title,
          previous: previousImage.value?.title || 'none',
          next: nextImage.value?.title || 'none'
        });
      } else {
        previousImage.value = null;
        nextImage.value = null;
        console.log('No current image, adjacent images cleared');
      }
    };
    
    // Call updateAdjacentImages immediately
    updateAdjacentImages();
    
    // And set up a watcher to update whenever the image prop changes
    vueWatch(() => props.image, () => {
      updateAdjacentImages();
    }, { immediate: true });
    
    // Computed styles for the layers based on swipe offset
    const topLayerStyle = computed(() => ({
      transform: `translateY(calc(-100% + ${swipeOffset.value}px))`,
      transition: isDragging.value ? 'none' : 'transform 0.3s ease-out'
    }));
    
    const visibleLayerStyle = computed(() => ({
      transform: `translateY(${swipeOffset.value}px)`,
      transition: isDragging.value ? 'none' : 'transform 0.3s ease-out'
    }));
    
    const bottomLayerStyle = computed(() => ({
      transform: `translateY(calc(100% + ${swipeOffset.value}px))`,
      transition: isDragging.value ? 'none' : 'transform 0.3s ease-out'
    }));

    // Touch event handlers
    const handleTouchStart = (e: TouchEvent) => {
      if (!props.image) return;
      isDragging.value = true;
      startY.value = e.touches[0].clientY;
      currentY.value = startY.value;
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging.value) return;
      currentY.value = e.touches[0].clientY;
      swipeOffset.value = currentY.value - startY.value;
      e.preventDefault(); // Prevent default scrolling
    };
    
    const handleTouchEnd = () => {
      if (!isDragging.value) return;
      
      const screenHeight = window.innerHeight;
      const threshold = screenHeight * swipeThreshold;
      
      if (swipeOffset.value > threshold) {
        // Swiped down - go to previous image
        swipeToPrevious();
      } else if (swipeOffset.value < -threshold) {
        // Swiped up - go to next image
        swipeToNext();
      } else {
        // Reset position
        swipeOffset.value = 0;
      }
      
      isDragging.value = false;
    };
    
    // Mouse event handlers (for desktop)
    const handleMouseDown = (e: MouseEvent) => {
      if (!props.image) return;
      isDragging.value = true;
      startY.value = e.clientY;
      currentY.value = startY.value;
      e.preventDefault();
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.value) return;
      currentY.value = e.clientY;
      swipeOffset.value = currentY.value - startY.value;
      e.preventDefault();
    };
    
    const handleMouseUp = () => {
      handleTouchEnd(); // Reuse the same logic
    };
    
    // Swipe to next image
    const swipeToNext = () => {
      console.log('Swiping to next image', { 
        current: props.image?.title, 
        next: nextImage.value?.title 
      });
      
      if (!props.image || !nextImage.value) {
        console.log('Cannot swipe: no next image available');
        swipeOffset.value = 0;
        return;
      }
      
      // Animate to complete the swipe
      swipeOffset.value = -window.innerHeight;
      
      // After animation completes, update the images
      setTimeout(() => {
        // Disable transitions temporarily
        isDragging.value = true;
        
        // Change the image and reset offset without animation
        emit('change-image', nextImage.value);
        swipeOffset.value = 0;
        
        // Re-enable transitions after a brief delay
        setTimeout(() => {
          isDragging.value = false;
          updateAdjacentImages();
        }, 50);
      }, 300);
    };
    
    // Swipe to previous image
    const swipeToPrevious = () => {
      console.log('Swiping to previous image', { 
        current: props.image?.title, 
        previous: previousImage.value?.title 
      });
      
      if (!props.image || !previousImage.value) {
        console.log('Cannot swipe: no previous image available');
        swipeOffset.value = 0;
        return;
      }
      
      // Animate to complete the swipe
      swipeOffset.value = window.innerHeight;
      
      // After animation completes, update the images
      setTimeout(() => {
        // Disable transitions temporarily
        isDragging.value = true;
        
        // Change the image and reset offset without animation
        emit('change-image', previousImage.value);
        swipeOffset.value = 0;
        
        // Re-enable transitions after a brief delay
        setTimeout(() => {
          isDragging.value = false;
          updateAdjacentImages();
        }, 50);
      }, 300);
    };

    const onClose = () => {
      emit('close');
    };

    return {
      swipeContainer,
      previousImage,
      nextImage,
      topLayerStyle,
      visibleLayerStyle,
      bottomLayerStyle,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      onClose
    };
  }
});
</script>

<style scoped>
/* Full screen image viewer styles */
.fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  overflow: hidden;
  touch-action: none; /* Prevent browser handling of touch events */
}

.close-button {
  position: fixed;
  top: 20px;
  left: 20px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1002; /* Above all layers */
  transition: background-color 0.3s;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.swipe-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.swipe-layer {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  will-change: transform; /* Optimize for animations */
}

.top-layer {
  transform: translateY(-100%);
}

.visible-layer {
  transform: translateY(0);
}

.bottom-layer {
  transform: translateY(100%);
}

.fullscreen-content {
  max-width: 90%;
  max-height: 90%;
  position: relative;
}

.fullscreen-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border: 2px solid white;
}

.fullscreen-caption {
  position: absolute;
  bottom: -40px;
  left: 0;
  width: 100%;
  color: white;
  text-align: center;
  padding: 10px;
}

.source-link {
  display: inline-block;
  margin-top: 5px;
  color: #42b983;
  text-decoration: none;
  padding: 5px 10px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
}

.source-link:hover {
  text-decoration: underline;
  background: rgba(0, 0, 0, 0.8);
}
</style>
